import React from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  CheckCircle2,
  Download as DownloadIcon,
  ExternalLink,
  Smartphone,
} from 'lucide-react';

const YouTubeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#FF0000" d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z" />
    <path fill="#FFFFFF" d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const betaBuilds = [
  {
    name: 'arm64-v8a',
    label: 'Most modern Android phones',
    description: 'Choose this if your phone is from the last several years.',
    fileName: 'app-arm64-v8a-debug-v1.1.36-code102.apk',
    url: 'https://panda-app-assets.s3.amazonaws.com/blurr/apks/debug/2026-07-06/app-arm64-v8a-debug-v1.1.36-code102.apk',
    checksum: '1cae234dffbdbe8ed7bafe1521d01c9767b428803b3942dcc1bb46b527c4f283',
    recommended: true,
  },
  {
    name: 'armeabi-v7a',
    label: 'Older 32-bit Android phones',
    description: 'Use this only if the modern-phone APK does not install.',
    fileName: 'app-armeabi-v7a-debug-v1.1.36-code102.apk',
    url: 'https://panda-app-assets.s3.amazonaws.com/blurr/apks/debug/2026-07-06/app-armeabi-v7a-debug-v1.1.36-code102.apk',
    checksum: '074683374e9243a51a6549a0b8db9d5e78be36a4d5c77356f49eb40f079a9a97',
    recommended: false,
  },
];

const accessibilityHelpUrl = 'https://support.google.com/android/answer/12623953?hl=en';
const youtubeGuideUrl = 'https://www.youtube.com/watch?v=oMVdI1CpAlM';

function DownloadBeta() {
  return (
    <main className="beta-download-page">
      <motion.section
        className="beta-download-hero"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <div className="new-hero-badge beta-download-badge">
          <span className="new-hero-badge-text">Beta Build</span>
          <span className="new-hero-badge-bars"><span /><span /></span>
        </div>

        <h1 className="beta-download-title">DOWNLOAD PANDA BETA</h1>
        <p className="beta-download-subtitle">
          This APK is for beta testers. The app may break, behave unexpectedly, or need extra setup
          before it works properly on your phone.
        </p>

        <div className="beta-warning-card">
          <AlertTriangle size={24} aria-hidden="true" />
          <div>
            <h2>Important beta warning</h2>
            <p>
              Panda needs critical Android permissions, including Accessibility, so it can understand
              and operate your phone interface. Android or Google Play Protect may show scary warnings
              because the beta asks for powerful permissions. This warning is based on the permission
              class and beta APK installation flow, not a confirmed malware finding.
            </p>
          </div>
        </div>
      </motion.section>

      <motion.section
        className="beta-download-grid"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        aria-label="Beta APK downloads"
      >
        {betaBuilds.map((build) => (
          <article className="beta-apk-card" key={build.name}>
            <div className="beta-apk-card-header">
              <Smartphone size={22} aria-hidden="true" />
              <div>
                <h2>{build.name}</h2>
                <p>{build.label}</p>
              </div>
            </div>

            {build.recommended && <span className="beta-recommended-pill">Recommended</span>}

            <p className="beta-apk-description">{build.description}</p>

            <a className="clip-btn clip-btn-accent beta-download-button" href={build.url}>
              <DownloadIcon size={18} aria-hidden="true" />
              Download APK
            </a>

            <div className="beta-file-meta">
              <span>File</span>
              <code>{build.fileName}</code>
            </div>

            <div className="beta-file-meta">
              <span>SHA-256 checksum</span>
              <code>{build.checksum}</code>
            </div>
          </article>
        ))}
      </motion.section>

      <motion.section
        className="beta-help-panel"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2 }}
      >
        <div className="beta-help-card">
          <div className="beta-help-card-icon">
            <GoogleIcon />
          </div>
          <h2>Accessibility permission blocked?</h2>
          <p>
            If Android does not let you grant Accessibility permission after installing the APK,
            follow Google's troubleshooting steps for restricted settings.
          </p>
          <a href={accessibilityHelpUrl} target="_blank" rel="noopener noreferrer" className="beta-brand-link">
            <GoogleIcon />
            Open Android troubleshooting guide
            <ExternalLink size={15} aria-hidden="true" />
          </a>
        </div>

        <div className="beta-help-card">
          <CheckCircle2 size={24} aria-hidden="true" />
          <h2>Before you install</h2>
          <ul>
            <li>Install only on a device where you are comfortable testing beta software.</li>
            <li>Prefer the arm64-v8a build unless you know your phone is 32-bit only.</li>
            <li>After installing, verify the permissions you grant and remove the app anytime from Android settings.</li>
          </ul>
        </div>

        <div className="beta-help-card beta-video-card">
          <div className="beta-help-card-icon">
            <YouTubeIcon />
          </div>
          <h2>YouTube setup guide</h2>
          <p>
            Watch the walkthrough to follow installation and permission setup step by step.
          </p>
          <a href={youtubeGuideUrl} target="_blank" rel="noopener noreferrer" className="beta-brand-link beta-brand-link-youtube">
            <YouTubeIcon />
            Watch setup guide on YouTube
            <ExternalLink size={15} aria-hidden="true" />
          </a>
        </div>
      </motion.section>
    </main>
  );
}

export default DownloadBeta;
